// Scenario Protocol Definitions based on PRD
export const SCENARIOS = [
  {
    id: 'high_stakes_negotiation',
    name: 'High-Stakes Negotiation',
    description: "Navigate complex deal dynamics with centered presence",
    stateSequence: ['ground', 'observe', 'connect', 'execute'],
    fluidityChallenge: "Don't enter in Execute - leads to deadlock.",
    duration: 15,
    phases: [
      {
        state: 'ground',
        name: 'Center Before Entry',
        duration: 180,
        instructions: [
          "Find your breath. Three slow cycles.",
          "Feel your feet. Weight distributed evenly.",
          "Release tension in your jaw, shoulders, hands.",
          "Set your intention: I enter from stillness, not urgency."
        ],
        reflection: "What outcome would serve everyone at this table?"
      },
      {
        state: 'observe',
        name: 'Read the Room',
        duration: 240,
        instructions: [
          "Notice energy levels - who's tense, who's open.",
          "Track body language without interpreting.",
          "Listen to what's NOT being said.",
          "Map the underlying interests, not positions."
        ],
        reflection: "What are they really protecting?"
      },
      {
        state: 'connect',
        name: 'Build the Bridge',
        duration: 240,
        instructions: [
          "Acknowledge their perspective genuinely.",
          "Find one thing you can agree on immediately.",
          "Use 'we' language to create shared ground.",
          "Let them feel heard before you advance."
        ],
        reflection: "Have I earned the right to propose?"
      },
      {
        state: 'execute',
        name: 'Make Your Move',
        duration: 180,
        instructions: [
          "State your proposal clearly, without hedging.",
          "Anchor to shared interests you established.",
          "Offer one meaningful concession strategically.",
          "Hold silence after your ask. Let it land."
        ],
        reflection: "Did I negotiate from strength or fear?"
      }
    ]
  },
  {
    id: 'difficult_feedback',
    name: 'Delivering Difficult Feedback',
    description: "Share hard truths while preserving the relationship",
    stateSequence: ['ground', 'observe', 'connect', 'integrate'],
    fluidityChallenge: "Message gets lost in Connect without Observe first.",
    duration: 14,
    phases: [
      {
        state: 'ground',
        name: 'Check Your State',
        duration: 180,
        instructions: [
          "Notice if you're carrying frustration or judgment.",
          "Breathe until you can see their humanity.",
          "Release the need to be right.",
          "Set intention: growth, not punishment."
        ],
        reflection: "Am I delivering this for them or for me?"
      },
      {
        state: 'observe',
        name: 'See the Full Picture',
        duration: 180,
        instructions: [
          "Recall specific behaviors, not character judgments.",
          "Consider context you might be missing.",
          "Notice your assumptions about their intentions.",
          "Separate impact from intent."
        ],
        reflection: "What might they be facing that I don't see?"
      },
      {
        state: 'connect',
        name: 'Create Safety',
        duration: 240,
        instructions: [
          "Start with genuine care for their success.",
          "Share the feedback as observation, not accusation.",
          "Use 'I noticed' rather than 'You always'.",
          "Invite their perspective before prescribing solutions."
        ],
        reflection: "Did they feel attacked or supported?"
      },
      {
        state: 'integrate',
        name: 'Land the Learning',
        duration: 180,
        instructions: [
          "Summarize the shared understanding.",
          "Co-create the path forward together.",
          "Express confidence in their ability to grow.",
          "End with commitment, not criticism."
        ],
        reflection: "What changed in our relationship through this?"
      }
    ]
  },
  {
    id: 'leading_crisis',
    name: 'Leading Through Crisis',
    description: "Stay clear when everything is on fire",
    stateSequence: ['ground', 'observe', 'execute', 'integrate'],
    fluidityChallenge: "Ground first under pressure - the critical discipline.",
    duration: 13,
    phases: [
      {
        state: 'ground',
        name: 'Stop the Spiral',
        duration: 180,
        instructions: [
          "STOP. Three breaths before any action.",
          "Name your fear without believing it.",
          "Feel your body. You are here. You are safe.",
          "Remember: Panic spreads. Calm spreads too."
        ],
        reflection: "What would I do if I wasn't afraid?"
      },
      {
        state: 'observe',
        name: 'Assess Reality',
        duration: 180,
        instructions: [
          "What do we actually KNOW vs. assume?",
          "Who needs to be involved in decisions?",
          "What's urgent vs. what feels urgent?",
          "What resources are available right now?"
        ],
        reflection: "Am I seeing clearly or reacting?"
      },
      {
        state: 'execute',
        name: 'Decide and Move',
        duration: 240,
        instructions: [
          "Choose ONE priority. Not five.",
          "Communicate the decision with clarity.",
          "Assign ownership. No shared accountability.",
          "Set a checkpoint. We'll reassess in [X]."
        ],
        reflection: "Did I give them clarity or confusion?"
      },
      {
        state: 'integrate',
        name: 'Process and Learn',
        duration: 180,
        instructions: [
          "Acknowledge the team's effort.",
          "Note what worked, without blame for what didn't.",
          "Identify one system to improve.",
          "Rest. Recovery is part of crisis leadership."
        ],
        reflection: "What did this crisis teach us?"
      }
    ]
  },
  {
    id: 'strategic_vision',
    name: 'Strategic Vision Setting',
    description: "See the future before the data confirms it",
    stateSequence: ['ground', 'integrate', 'observe', 'execute'],
    fluidityChallenge: "Wisdom before analysis, not after.",
    duration: 15,
    phases: [
      {
        state: 'ground',
        name: 'Empty the Cup',
        duration: 180,
        instructions: [
          "Release attachment to current strategy.",
          "Acknowledge what you don't want to see.",
          "Let go of looking smart.",
          "Create space for insight to emerge."
        ],
        reflection: "What am I holding too tightly?"
      },
      {
        state: 'integrate',
        name: 'Access Wisdom',
        duration: 240,
        instructions: [
          "What does your intuition say, before logic?",
          "What patterns are you sensing across domains?",
          "If you knew you couldn't fail, what would you build?",
          "What would the 10-year version of you decide?"
        ],
        reflection: "What do I know but haven't admitted?"
      },
      {
        state: 'observe',
        name: 'Test Against Reality',
        duration: 240,
        instructions: [
          "Now look at the data. Does it support or contradict?",
          "What signals might you be missing?",
          "Who would challenge this vision most?",
          "What's the cost of being wrong?"
        ],
        reflection: "Where might I be fooling myself?"
      },
      {
        state: 'execute',
        name: 'Crystallize and Commit',
        duration: 180,
        instructions: [
          "Articulate the vision in one clear sentence.",
          "Identify the first irreversible step.",
          "Name who needs to hear this first.",
          "Set the timeline for public commitment."
        ],
        reflection: "Am I ready to lead others there?"
      }
    ]
  },
  {
    id: 'team_conflict',
    name: 'Managing Team Conflict',
    description: "Transform friction into productive tension",
    stateSequence: ['observe', 'ground', 'connect', 'execute'],
    fluidityChallenge: "Observe first - your read of the conflict is probably wrong.",
    duration: 14,
    phases: [
      {
        state: 'observe',
        name: 'See Before Solving',
        duration: 240,
        instructions: [
          "Watch the dynamic without intervention.",
          "Notice who speaks, who stays silent.",
          "Track the underlying emotion, not the content.",
          "Resist the urge to fix immediately."
        ],
        reflection: "What is this conflict really about?"
      },
      {
        state: 'ground',
        name: 'Check Your Bias',
        duration: 180,
        instructions: [
          "Notice who you're unconsciously siding with.",
          "Release your preferred outcome.",
          "Breathe until you can hold both perspectives.",
          "Remember: your intervention will shape the team."
        ],
        reflection: "Am I neutral or have I already decided?"
      },
      {
        state: 'connect',
        name: 'Create the Container',
        duration: 240,
        instructions: [
          "Acknowledge the tension without judgment.",
          "Invite each perspective to be heard.",
          "Reflect back what you hear without interpretation.",
          "Find the shared value underneath the disagreement."
        ],
        reflection: "Did both sides feel genuinely heard?"
      },
      {
        state: 'execute',
        name: 'Move to Resolution',
        duration: 180,
        instructions: [
          "Name the decision that needs to be made.",
          "Clarify who has final authority (if not consensus).",
          "Set clear expectations going forward.",
          "Schedule a follow-up to ensure resolution holds."
        ],
        reflection: "Did I resolve the issue or just suppress it?"
      }
    ]
  },
  {
    id: 'board_presentation',
    name: 'Board / ExCom Presentation',
    description: "Command the room before you open your mouth",
    stateSequence: ['ground', 'observe', 'execute', 'integrate'],
    fluidityChallenge: "The room reads your state before you speak.",
    duration: 14,
    phases: [
      {
        state: 'ground',
        name: 'Own the Room',
        duration: 180,
        instructions: [
          "Before entering, stand tall. Three deep breaths.",
          "Feel your competence. You belong here.",
          "Release the need for approval.",
          "Enter with presence, not performance."
        ],
        reflection: "Do I believe in what I'm about to say?"
      },
      {
        state: 'observe',
        name: 'Read the Board',
        duration: 180,
        instructions: [
          "Scan the room before you begin.",
          "Notice who's skeptical, who's supportive.",
          "Adjust your energy to meet theirs.",
          "Identify the decision-maker's state."
        ],
        reflection: "What does this room need from me right now?"
      },
      {
        state: 'execute',
        name: 'Deliver with Clarity',
        duration: 300,
        instructions: [
          "Lead with the ask, not the context.",
          "Speak in headlines, not paragraphs.",
          "Make eye contact with the skeptics.",
          "Pause after key points. Let them land."
        ],
        reflection: "Was I clear or was I thorough?"
      },
      {
        state: 'integrate',
        name: 'Close the Loop',
        duration: 180,
        instructions: [
          "Summarize the decision or next step.",
          "Thank them for their time and input.",
          "Note feedback for future presentations.",
          "Release the outcome. You showed up fully."
        ],
        reflection: "What did I learn about this audience?"
      }
    ]
  }
];

// Prepare mode vs Reflect mode context
export const SCENARIO_MODES = {
  prepare: {
    id: 'prepare',
    label: 'Prepare',
    description: 'Use before a real situation',
    completionMessage: "You're ready."
  },
  reflect: {
    id: 'reflect',
    label: 'Reflect',
    description: 'Use after a situation that did not go as intended',
    completionMessage: "Learning logged."
  }
};

export default SCENARIOS;
